/**
 * Geometry for the Financials pipeline iceberg: an inverted triangle, starred-
 * prospect upside on top and active-deal backlog below, one horizontal slice
 * per company. Slice HEIGHT is proportional to the fee, the same encoding as
 * the mademarket chart Caleb picked it from; every slice is labeled directly,
 * so nobody has to judge a trapezoid's area.
 */
export interface IcebergInput { id: string; name: string; amount: number; tier: "upside" | "backlog"; sub: string }
export type IcebergSlice = IcebergInput & { y0: number; y1: number };

export function icebergSlices(items: IcebergInput[], top: number, height: number): IcebergSlice[] {
  const shown = items
    .filter((i) => i.amount > 0)
    .sort((a, b) => (a.tier === b.tier ? b.amount - a.amount : a.tier === "upside" ? -1 : 1));
  const total = shown.reduce((s, i) => s + i.amount, 0);
  let y = top;
  return shown.map((i) => {
    const y0 = y;
    y += (i.amount / total) * height;
    return { ...i, y0, y1: y };
  });
}

/** Half-width of the triangle at y; the tip is at top + height. */
export function halfWidthAt(y: number, top: number, height: number, halfTop: number): number {
  if (height <= 0) return 0;
  return Math.max(0, (halfTop * (top + height - y)) / height);
}

/**
 * Label y positions: keep order, keep at least `gap` apart, stay in [minY, maxY].
 * Forward pass pushes down, backward pass pulls up from the bottom edge. If
 * they cannot all fit, spread them evenly instead.
 */
export function placeLabels(anchors: number[], gap: number, minY: number, maxY: number): number[] {
  const n = anchors.length;
  if (n === 0) return [];
  if (n === 1) return [Math.min(Math.max(anchors[0], minY), maxY)];
  if ((n - 1) * gap > maxY - minY) return anchors.map((_, i) => minY + (i * (maxY - minY)) / (n - 1));
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(Math.max(anchors[i], i ? out[i - 1] + gap : minY));
  for (let i = n - 1; i >= 0; i--) out[i] = Math.min(out[i], i === n - 1 ? maxY : out[i + 1] - gap);
  return out;
}

/** `text` if it fits `maxPx`, else the longest prefix plus an ellipsis that fits, else "". */
export function truncateToWidth(text: string, maxPx: number, measure: (s: string) => number): string {
  if (measure(text) <= maxPx) return text;
  if (measure("…") > maxPx) return "";
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (measure(text.slice(0, mid).trimEnd() + "…") <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo).trimEnd() + "…";
}
